import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import find from 'lodash/find';
import map from 'lodash/map';
import trim from 'lodash/trim';
import User from 'yoda-site-components/lib/helpers/User/User';
import ShippingService from '../services/ShippingService';
import * as actionTypes from '../actionTypes/NewShippingActionTypes';
import PaymentConstants from '../helpers/PaymentConstants';
import { skipShippingMethodSelection } from '../helpers/Utils/Utils';

/**
 * Reducer Structure
 * shipping: {
 *     shippingType: 'HOME/STORE',
 *     isShipToHomeOnly: true/false,
 *     isLoadedFromReview: true/false,
 *     showContinueCTA: true/false,
 *     showContinueToPaymentCTA: true/false,
 *     showContinueToReviewCTA: true/false,
 *     showCancelCTA: true/false,
 *     isTruckableItemInOrder: true/false,
 *     skipShippingMethodSelections: true/false,
 *     shipToHomeInfo: {
 *          userInputAddress: {
 *              address: {
 *                  firstName: '',
 *                  lastName: '',
 *                  lineOne: '',
 *                  lineTwo: '',
 *                  zip: '',
 *                  city: '',
 *                  state: '',
 *                  phone: '',
 *                  militaryAddressType: '',
 *                  country: '',
 *                  expressCheckout: true/false,
 *              },
 *              addressTouchedFields: {
 *                  firstName: false/true,
 *                  lastName: false/true,
 *                  lineOne: false/true,
 *                  lineTwo: false/true,
 *                  zip: false/true,
 *                  city: false/true,
 *                  state: false/true,
 *                  phone: false/true,
 *                  country: false/true,
 *              },
 *              addressFormErrors:{
 *                  firstName: '',
 *                  lastName: '',
 *                  lineOne: '',
 *                  lineTwo: '',
 *                  zip: '',
 *                  city: '',
 *                  state: '',
 *                  phone: '',
 *                  country: '',
 *             },
 *             usStateList: [],
 *             militaryAddressTypeList: [],
 *             showCompanyName: true/false,
 *             showMilitaryAddressFields: true/false,
 *             showExpressCheckoutCheckBox: true/false,
 *             isBillingAddressForm: true/false,
 *             isInternationalAddress: true/false,
 *             isStreetAddressCheckExecuted: true/false,
 *             isZipInvalidFromAPI: true/ false
 *         },
 *         xStandardizationNotRequired: true/false,
 *         showAddShippingAddressForm: true/false,
 *         showAddNewAddressLink: true/false,
 *         showViewMoreAddressLink: true/false,
 *         showHolidayShippingMsg: true/false,
 *         shippingMethods: [{
 *             label: 'duration - type',
 *             charge: 'free or $12',
 *             message: 'message',
 *             selected: 'true/false',
 *             code: 'code',
 *             isFastestDelivery: true/false,
 *         },...],
 *         showTruckDeliveryInfo: false/true,
 *          hideDeliveryInfo: true/false,
 *         userAddressList: [{
 *             userName: 'firstName lastName',
 *             addressLineOne: 'lineOne, lineTwo',
 *             addressLineTwo: 'city, state, c',
 *             mobileNo: 'phone',
 *             showDefaultLabel: 'true/false',
 *             showDeleteOption: 'true/false',
 *             selected: 'true/false',
 *             source,
 *         }],
 *         deleteShippingAddressInfo: {
 *             showWarning: 'true/false',
 *             shippingAddressId: '',
 *         }
 *     },
 *     shipToStoreInfo: {
 *          showAddNewStoreOption: true/false,
 *          pickupPersonInfo: {
 *              firstName: '',
 *              lastName: '',
 *              phone: '',
 *          },
 *          pickupPersonInfoErrors: {
 *              firstName: '',
 *              lastName: '',
 *              phone: '',
 *          },
 *          pickupPersonInfoTouchedFields: {
 *              firstName: '',
 *              lastName: '',
 *              phone: '',
 *          },
 *          shippingMethod: {
 *              label: 'duration - type',
 *              message: 'message',
 *          },
 *          pickUpStoreList: [{
 *              storeId: '',
 *              name: '',
 *              addressLineOne: '',
 *              addressLineTwo: '',
 *              phone: '',
 *              timings: [],
 *              selected: true/false,
 *              isMyStore: true/false,
 *          }],
 *
 *     }
 * }
 */

export default function NewShippingReducer(state = {}, action) {
    // NOSONAR
    switch (
        action.type // NOSONAR
    ) {
        /**
         * Initialize the shipping section for guest and register user
         */
        case actionTypes.INIT_SHIP_TO_HOME_SECTION_UI: {
            const { orderDetails, addressList, isLoadedFromReview, context } = action;
            const items = get(orderDetails, 'data.bag.items');
            const isTruckableItemInOrder = ShippingService.checkTruckableItemInBag(items);
            const skipShippingMethodSelections = skipShippingMethodSelection(context, orderDetails);
            const updatedState = {
                shippingType: 'HOME',
                isShipToHomeOnly: ShippingService.checkShipToHomeOnly(items),
                isTruckableItemInOrder,
                isLoadedFromReview,
                skipShippingMethodSelections,
            };
            const orderShippingAddress = ShippingService.getOrderShipToHomeAddress(orderDetails);
            const isUserLoggedIn = !!addressList;
            const exceptions = get(orderDetails, 'data.status.exceptions', []);
            const shippingException = find(exceptions, {
                errorCode: 'API_ERR_FIELD_HOME_INTL_COUNTRY_NOT_SUPPORTED',
            });
            // adding this new logic to filter out all exception where customer need to be stopped in shipping section
            // const validShippingException = find(exceptions, {
            //     errorCode: 'SRV_CUSTOMER_ADDRESS_ONE_INVALID',
            // });

            if (
                (isEmpty(orderShippingAddress.address) && isEmpty(addressList)) ||
                shippingException
            ) {
                /**
                 * Initialize STH form
                 */
                const shipToHomeUserInputAddress = get(
                    state,
                    'shipToHomeInfo.userInputAddress.address',
                    {}
                );
                // const subPremiseInvalid = get(state, 'shipToHomeInfo.subPremiseInvalid', false);
                updatedState.shipToHomeInfo = {
                    userInputAddress: ShippingService.getEmptySTHFormInfo(
                        false,
                        false,
                        isTruckableItemInOrder,
                        isUserLoggedIn,
                        orderShippingAddress.address || shipToHomeUserInputAddress
                    ),
                    xStandardizationNotRequired: false,
                    showAddShippingAddressForm: true,
                    // subPremiseInvalid,
                };
                if (!isEmpty(shipToHomeUserInputAddress)) {
                    Object.assign(
                        updatedState.shipToHomeInfo.userInputAddress.address,
                        shipToHomeUserInputAddress
                    );
                }
                // if (validShippingException) {
                //     Object.assign(
                //         updatedState.shipToHomeInfo.userInputAddress.address,
                //         orderShippingAddress.address
                //     );
                // }
                const shipToStorePickupPersonInfo = get(
                    state,
                    'shipToStoreInfo.pickupPersonInfo',
                    {}
                );
                if (!isEmpty(shipToStorePickupPersonInfo)) {
                    Object.assign(
                        updatedState.shipToHomeInfo.userInputAddress.address,
                        shipToStorePickupPersonInfo
                    );
                }
                updatedState.showContinueCTA = true;
                updatedState.showContinueToPaymentCTA = false;
                updatedState.showContinueToReviewCTA = false;
                if (skipShippingMethodSelections) {
                    const serviceLevels = get(orderShippingAddress, 'serviceLevels.data', []);
                    if (!isEmpty(serviceLevels)) {
                        updatedState.shipToHomeInfo.shippingMethods = ShippingService.getOrderShippingMethodUI(
                            orderShippingAddress.serviceLevels
                        );
                    } else {
                        updatedState.skipShippingMethodSelections = false;
                    }
                }
            } else {
                const serviceLevels = get(orderShippingAddress, 'serviceLevels.data', []);
                updatedState.showContinueCTA = isEmpty(serviceLevels);
                updatedState.showContinueToPaymentCTA =
                    !updatedState.showContinueCTA && !isLoadedFromReview;
                updatedState.showContinueToReviewCTA =
                    !updatedState.showContinueCTA && isLoadedFromReview;
                if (skipShippingMethodSelections && isEmpty(serviceLevels)) {
                    updatedState.skipShippingMethodSelections = false;
                }
                /**
                 * Initialize order shipping address sections
                 */
                let userAddressList = [];
                const orderAddress = orderShippingAddress.address;
                let showAddNewAddressLink = false;
                if (!isEmpty(orderAddress) && isEmpty(addressList)) {
                    /**
                     * Guest user list address from order
                     */
                    const orderAddressUI = ShippingService.getOrderShippingAddressUI(orderAddress);
                    orderAddressUI.selected = true;
                    userAddressList.push(orderAddressUI);
                }
                if (isUserLoggedIn) {
                    /**
                     * Register user list address from profile and merge order address
                     */
                    showAddNewAddressLink = true;
                    userAddressList = ShippingService.getRegisterUserShippingAddressUI(
                        addressList,
                        orderAddress
                    );
                }
                let shippingMethods = [];
                let showTruckDeliveryInfo = false;
                let showHolidayShippingMsg = false;
                if (!isEmpty(serviceLevels)) {
                    shippingMethods = ShippingService.getOrderShippingMethodUI(
                        orderShippingAddress.serviceLevels
                    );
                    showTruckDeliveryInfo = ShippingService.isTruckShippingMethod(
                        orderShippingAddress.serviceLevels
                    );
                    showHolidayShippingMsg = ShippingService.isHolidayShippingEligible(
                        orderShippingAddress.serviceLevels
                    );
                }
                const showViewMoreAddressLink = ShippingService.checkShowMoreAddressLink(
                    userAddressList
                );
                if (showViewMoreAddressLink) {
                    showAddNewAddressLink = false;
                }
                let subPremiseInvalid = false;
                if (isUserLoggedIn) {
                    subPremiseInvalid = get(state, 'shipToHomeInfo.subPremiseInvalid', false);
                }

                updatedState.shipToHomeInfo = {
                    shippingMethods,
                    showTruckDeliveryInfo,
                    xStandardizationNotRequired: false,
                    showAddShippingAddressForm: false,
                    showAddNewAddressLink,
                    userAddressList,
                    showViewMoreAddressLink,
                    showHolidayShippingMsg,
                    // subPremiseInvalid,
                };
                updatedState.showCancelCTA = false;
            }
            return { ...state, ...updatedState };
        }

        /**
         * Initialize ship to store section
         */
        case actionTypes.NEW_INIT_SHIP_STORE_SECTION_UI: {
            const {
                orderDetails,
                storeInfo,
                userStoreList,
                isUserLoggedIn,
                isLoadedFromReview,
            } = action;
            let { shipToStoreInfo } = state;
            /*
             * Pulling pickup person info from shipToHome due to on the first load, STS info would be empty
             * */
            const userInputAddress = get(state, 'shipToHomeInfo.userInputAddress.address', {});
            let pickUpStoreList;
            if (!shipToStoreInfo) {
                shipToStoreInfo = ShippingService.initShipToStoreSectionUI(
                    orderDetails,
                    isUserLoggedIn
                );
                pickUpStoreList = ShippingService.getPickupStoreList(storeInfo, userStoreList);
            } else {
                pickUpStoreList = shipToStoreInfo.pickUpStoreList;
                shipToStoreInfo = ShippingService.initShipToStoreSectionUI(
                    orderDetails,
                    isUserLoggedIn
                );
            }
            shipToStoreInfo.pickupPersonInfoErrors = {};
            const updatedState = {
                shippingType: 'STORE',
                showContinueToPaymentCTA: !isLoadedFromReview,
                showContinueToReviewCTA: isLoadedFromReview,
                showContinueCTA: false,
                showCancelCTA: false,
                shipToStoreInfo,
                isLoadedFromReview,
            };
            updatedState.shipToStoreInfo.pickUpStoreList = pickUpStoreList;
            const orderSTSAddress = ShippingService.getOrderShipToStoreAddress(orderDetails);
            const orderShippingAddress = ShippingService.getOrderShippingAddress(orderDetails);
            updatedState.shipToStoreInfo.pickupPersonInfo = ShippingService.getPickupPersonInfo(
                orderSTSAddress.pickup || orderShippingAddress.address || userInputAddress
            );
            return { ...state, ...updatedState };
        }

        /**
         * Update shipping address form based on user input
         */
        case actionTypes.UPDATE_SHIPPING_ADDRESS_FORM: {
            const { attribute } = action;
            let newValue = action.newValue || '';
            let { address } = state.shipToHomeInfo.userInputAddress;
            const { addressTouchedFields } = state.shipToHomeInfo.userInputAddress;
            const { addressFormErrors } = state.shipToHomeInfo.userInputAddress;
            const oldValue = address[attribute];
            let { usStateList } = state.shipToHomeInfo.userInputAddress;
            const { isBillingAddressForm } = state.shipToHomeInfo.userInputAddress;
            let { isInternationalAddress } = state.shipToHomeInfo.userInputAddress;
            if (attribute === 'phone' && !isEmpty(newValue)) {
                if (newValue.length < PaymentConstants.PHONE_NUMBER_MAX_LENGTH) {
                    newValue = ShippingService.getFormattedPhoneNumber(newValue);
                } else {
                    newValue = address.phone;
                }
            }

            address[attribute] = newValue;
            if (attribute === 'zip' && newValue.length > 5 && !isInternationalAddress) {
                address.zip = newValue.slice(0, 5);
            }
            /**
             * Reset error if input value is correct format
             */
            const errorMessage = ShippingService.validateShippingFormField(attribute, newValue);
            if (addressTouchedFields[attribute] || isEmpty(errorMessage)) {
                addressFormErrors[attribute] = errorMessage;
            }
            /** Reset 'lineTwo' & 'subPremiseInvalid' to optional on change of anyother attribute */
            let subPremiseInvalid = state?.shipToHomeInfo?.subPremiseInvalid;
            if (
                state?.shipToHomeInfo?.subPremiseInvalid &&
                (attribute === 'lineOne' ||
                    attribute === 'country' ||
                    attribute === 'zip' ||
                    attribute === 'state')
            ) {
                const errorMessage = ShippingService.validateShippingFormField(
                    'lineTwo',
                    address.lineTwo
                );
                if (addressTouchedFields.lineTwo || isEmpty(errorMessage)) {
                    addressFormErrors.lineTwo = errorMessage;
                }
                subPremiseInvalid = false;
            }
            /**
             * Clear address and update states if country changed from US to AP or AP to US
             */
            if (
                !isBillingAddressForm &&
                attribute === 'country' &&
                (oldValue === 'US' || newValue === 'US')
            ) {
                address = ShippingService.clearAddressField(address);
                if (newValue === 'US') {
                    delete address.militaryAddressType;
                }
                usStateList =
                    newValue === 'US'
                        ? ShippingService.getUSStateList()
                        : ShippingService.getArmedForcesStateList();
            }
            /**
             * Should not update state value if invalid state for APO/FPO/DPO
             */
            if (attribute === 'state' && address.country !== 'US') {
                const apoStateMap = ['AA', 'AE', 'AP'];
                if (
                    !ShippingService.isInternationalAddress(address.country) &&
                    apoStateMap.indexOf(newValue) === -1
                ) {
                    address.state = '';
                }
            }
            if (isBillingAddressForm && attribute === 'country') {
                isInternationalAddress = false;
                const isOldValueeMilitaryAddress = ShippingService.isMilitaryAddress(oldValue);
                const isNewValueeMilitaryAddress = ShippingService.isMilitaryAddress(newValue);
                if (
                    oldValue === 'US' ||
                    newValue === 'US' ||
                    !(isOldValueeMilitaryAddress && isNewValueeMilitaryAddress)
                ) {
                    address = ShippingService.clearAddressField(address);
                }

                if (newValue === 'US') {
                    usStateList = ShippingService.getUSStateList();
                } else if (newValue === 'APO' || newValue === 'FPO' || newValue === 'DPO') {
                    usStateList = ShippingService.getArmedForcesStateList();
                } else if (newValue === 'CA') {
                    usStateList = ShippingService.getCanadaStateList();
                    isInternationalAddress = true;
                } else {
                    address.state = '';
                    usStateList = [];
                    isInternationalAddress = true;
                }
            }

            let xGoogleStandardizationNotRequired = get(
                state,
                'shipToHomeInfo.xGoogleStandardizationNotRequired',
                false
            );
            if (xGoogleStandardizationNotRequired) {
                const isRequiredAttributeValueChanged =
                    attribute === 'lineOne' ||
                    attribute === 'lineTwo' ||
                    attribute === 'country' ||
                    attribute === 'zip' ||
                    attribute === 'state';
                if (isRequiredAttributeValueChanged && oldValue !== newValue) {
                    xGoogleStandardizationNotRequired = false;
                }
            }
            const userInputAddress = {
                ...state.shipToHomeInfo.userInputAddress,
                address,
                addressFormErrors,
                usStateList,
                isInternationalAddress,
            };
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                userInputAddress,
                subPremiseInvalid,
                xGoogleStandardizationNotRequired,
            };
            return { ...state, shipToHomeInfo };
        }

        /**
         * Update shipping address form error if user tab on continue button
         */
        case actionTypes.UPDATE_SHIPPING_ADDRESS_FORM_ERROR: {
            const addressFormErrors = action.errorInfo;
            const userInputAddress = {
                ...get(state, 'shipToHomeInfo.userInputAddress', {}),
                addressFormErrors,
            };
            const shipToHomeInfo = { ...state.shipToHomeInfo, userInputAddress };
            return { ...state, shipToHomeInfo };
        }

        /**
         * Update credit card form error based on user input
         */
        case actionTypes.UPDATE_SHIPPING_ADDRESS_FIELD_ERROR: {
            const { attribute } = action;
            const { addressFormErrors } = state.shipToHomeInfo?.userInputAddress || {};
            const { addressTouchedFields } = state.shipToHomeInfo?.userInputAddress || {};
            const { subPremiseInvalid } = state.shipToHomeInfo || {};

            const newValue = trim(state.shipToHomeInfo.userInputAddress.address[attribute]);
            let errorMessage = ShippingService.validateShippingFormField(attribute, newValue);
            if (subPremiseInvalid && attribute === 'lineTwo') {
                errorMessage = ShippingService.validateShippingFormField(
                    attribute,
                    newValue,
                    false,
                    true
                );
            }
            if (
                errorMessage === addressFormErrors[attribute] &&
                addressTouchedFields[attribute] === true
            ) {
                return state;
            }
            if (errorMessage) {
                addressFormErrors[attribute] = errorMessage;
            }
            addressTouchedFields[attribute] = true;
            const addressErrorInfo = { addressFormErrors, addressTouchedFields };
            const userInputAddress = {
                ...state.shipToHomeInfo.userInputAddress,
                ...addressErrorInfo,
            };
            const shipToHomeInfo = { ...state.shipToHomeInfo, userInputAddress };
            return { ...state, shipToHomeInfo };
        }

        case 'UPDATE_ZIP_ERROR': {
            const { errorMessage = '' } = action;
            const { addressFormErrors } = state.shipToHomeInfo.userInputAddress;
            const addressErrorInfo = { addressFormErrors };
            let isZipInvalidFromAPI = false;
            if (errorMessage) {
                addressFormErrors.zip = errorMessage;
                isZipInvalidFromAPI = true;
            } else {
                addressFormErrors.zip = '';
            }
            const userInputAddress = {
                ...state.shipToHomeInfo.userInputAddress,
                ...addressErrorInfo,
                isZipInvalidFromAPI,
            };
            const shipToHomeInfo = { ...state.shipToHomeInfo, userInputAddress };

            return { ...state, shipToHomeInfo };
        }

        /**
         * Update shipping address form based on user input
         */
        case actionTypes.UPDATE_STS__FORM: {
            const { attribute } = action;
            let { newValue } = action;
            const pickupPersonInfo = { ...state.shipToStoreInfo.pickupPersonInfo };
            const { pickupPersonInfoTouchedFields } = state.shipToStoreInfo;
            const pickupPersonInfoErrors = { ...state.shipToStoreInfo.pickupPersonInfoErrors };
            if (attribute === 'phone') {
                if (newValue.length < PaymentConstants.PHONE_NUMBER_MAX_LENGTH) {
                    newValue = ShippingService.getFormattedPhoneNumber(newValue);
                } else {
                    newValue = pickupPersonInfo.phone;
                }
            }
            pickupPersonInfo[attribute] = newValue;
            /**
             * Reset error if input value is correct format
             */
            const errorMessage = ShippingService.validateShippingFormField(attribute, newValue);
            if (pickupPersonInfoTouchedFields[attribute] || isEmpty(errorMessage)) {
                pickupPersonInfoErrors[attribute] = errorMessage;
            }
            const shipToStoreInfo = {
                ...state.shipToStoreInfo,
                pickupPersonInfo,
                pickupPersonInfoErrors,
            };
            return { ...state, shipToStoreInfo };
        }

        /**
         * Update shipping address form error if user tab on continue button
         */
        case actionTypes.UPDATE_STS__FORM_ERROR: {
            const pickupPersonInfoErrors = action.errorInfo;
            const shipToStoreInfo = { ...state.shipToStoreInfo, pickupPersonInfoErrors };
            return { ...state, shipToStoreInfo };
        }

        /**
         * Update credit card form error based on user input
         */
        case actionTypes.UPDATE_STS__FIELD_ERROR: {
            const { attribute } = action;
            const pickupPersonInfoErrors = { ...state.shipToStoreInfo.pickupPersonInfoErrors };
            const pickupPersonInfoTouchedFields = {
                ...state.shipToStoreInfo.pickupPersonInfoTouchedFields,
            };
            const newValue = state.shipToStoreInfo.pickupPersonInfo[attribute];
            const errorMessage = ShippingService.validateShippingFormField(attribute, newValue);
            if (
                errorMessage === pickupPersonInfoErrors[attribute] &&
                pickupPersonInfoTouchedFields[attribute] === true
            ) {
                return state;
            }
            if (errorMessage) {
                pickupPersonInfoErrors[attribute] = errorMessage;
            }
            pickupPersonInfoTouchedFields[attribute] = true;
            const shipToStoreInfo = {
                ...state.shipToStoreInfo,
                pickupPersonInfoErrors,
                pickupPersonInfoTouchedFields,
            };
            return { ...state, shipToStoreInfo };
        }

        /**
         * Update user selected store from user store list
         */
        case actionTypes.SELECT_STORE_FROM_PROFILE: {
            const selectedStoreId = action.storeId;
            const userStoreList = state.shipToStoreInfo.pickUpStoreList;
            const pickUpStoreList = ShippingService.updateSelectedStoreUI(
                selectedStoreId,
                userStoreList
            );
            const shipToStoreInfo = { ...state.shipToStoreInfo, pickUpStoreList };
            return { ...state, shipToStoreInfo };
        }

        /**
         * User added store into profile - update into UI
         */
        case actionTypes.PROFILE_ADDED_STORE_UPDATE_UI: {
            const { userStoreList } = action;
            const addedStoreId = action.storeId;
            const pickUpStoreList = ShippingService.getPickupStoreList(
                {},
                userStoreList,
                addedStoreId
            );
            const shipToStoreInfo = { ...state.shipToStoreInfo, pickUpStoreList };
            return { ...state, shipToStoreInfo };
        }

        /**
         * Update change store information in UI for guest user flow
         */
        case actionTypes.CHANGE_STS_UPDATE_UI: {
            const storeInfo = ShippingService.getStoreInfoUI(action.storeInfo);
            const pickUpStoreList = ShippingService.getPickupStoreList(storeInfo, []);
            const shipToStoreInfo = { ...state.shipToStoreInfo, pickUpStoreList };
            return { ...state, shipToStoreInfo };
        }

        /**
         * set address standardization required or not
         */
        case actionTypes.SET_NEW_SHIPPING_ADDRESS_STAND_NOT_REQUIRED: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                xStandardizationNotRequired: action.value,
            };
            return { ...state, shipToHomeInfo };
        }
        /**
         * set Google address standardization or not
         */
        case actionTypes.SET_NEW_SHIPPING_GOOGLE_ADDRESS_STAND_NOT_REQUIRED: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                xGoogleStandardizationNotRequired: action.value,
            };
            return { ...state, shipToHomeInfo };
        }
        /**
         * set address SubpremiseInvalid or not
         */
        case actionTypes.SET_NEW_SHIPPING_ADDRESS_SUBPREMISE_INVALID: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                subPremiseInvalid: action.value,
            };
            return { ...state, shipToHomeInfo };
        }

        case actionTypes.SET_GAV_ERROR: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                gavError: action.errorHandlerInfo,
            };
            return { ...state, shipToHomeInfo };
        }

        case actionTypes.CLEAR_GAV_ERROR: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                gavError: null,
                subPremiseInvalid: false,
            };
            return { ...state, shipToHomeInfo };
        }
        /**
         * Update shipping method
         */
        case actionTypes.UPDATE_SHIPPING_METHOD: {
            const { code } = action;
            const shippingMethodList = state.shipToHomeInfo.shippingMethods;
            const updatedShippingMethods = ShippingService.updateSelectedShippingMethod(
                shippingMethodList,
                code
            );
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                shippingMethods: updatedShippingMethods,
            };
            return { ...state, shipToHomeInfo };
        }

        /**
         * Update shipping method
         */
        case actionTypes.CHANGE_SHIPPING_ADDRESS_UI: {
            const { selectedShippingAddress } = action;
            const { orderDetails } = action;
            const { userAddressList } = state.shipToHomeInfo;
            const updatedUserAddressList = ShippingService.updateSelectedShippingAddressUI(
                userAddressList,
                selectedShippingAddress
            );
            const orderShippingAddress = ShippingService.getOrderShipToHomeAddress(orderDetails);
            const serviceLevels = get(orderShippingAddress, 'serviceLevels.data', []);
            const showContinueCTA = isEmpty(serviceLevels);
            const showContinueToPaymentCTA = !showContinueCTA && !state.isLoadedFromReview;
            const showContinueToReviewCTA = !showContinueCTA && state.isLoadedFromReview;
            const shippingMethods =
                orderShippingAddress &&
                orderShippingAddress.serviceLevels &&
                ShippingService.getOrderShippingMethodUI(orderShippingAddress.serviceLevels);
            const showTruckDeliveryInfo =
                orderShippingAddress &&
                orderShippingAddress.serviceLevels &&
                ShippingService.isTruckShippingMethod(orderShippingAddress.serviceLevels);

            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                userAddressList: updatedUserAddressList,
                shippingMethods,
                showTruckDeliveryInfo,
                hideDeliveryInfo: false,
            };
            const items = get(action.orderDetails, 'data.bag.items');
            const isShipToHomeOnly = ShippingService.checkShipToHomeOnly(items);
            return {
                ...state,
                shipToHomeInfo,
                showContinueCTA,
                showContinueToPaymentCTA,
                showContinueToReviewCTA,
                isShipToHomeOnly,
            };
        }

        /**
         * Show edit shipping address form with address
         */
        case actionTypes.EDIT_SHIPPING_ADDRESS: {
            const { address } = action;
            const isUserLoggedIn = User.isUserLoggedIn();

            const userInputAddress = ShippingService.getEmptySTHFormInfo(
                false,
                false,
                state.isTruckableItemInOrder,
                isUserLoggedIn,
                address.source
            );

            userInputAddress.address = { ...address.source };
            const phoneNumber = get(userInputAddress, 'address.phone');
            const country = get(userInputAddress, 'address.country');
            if (country === 'AP') {
                userInputAddress.address.country = userInputAddress.address.militaryAddressType;
                userInputAddress.usStateList = ShippingService.getArmedForcesStateList();
            } else if (country !== 'US') {
                userInputAddress.isInternationalAddress = true;
            }
            userInputAddress.address.phone = ShippingService.getFormattedPhoneNumber(phoneNumber);
            // const { subPremiseInvalid } = state.shipToHomeInfo || {};
            // const addressFormErrors = get(userInputAddress, 'addressFormErrors', {});
            // if (subPremiseInvalid) {
            //     const errorMessage = ShippingService.validateShippingFormField(
            //         'lineTwo',
            //         address.lineTwo,
            //         false,
            //         true
            //     );
            //     if (!isEmpty(errorMessage)) {
            //         addressFormErrors.lineTwo = errorMessage;
            //     }
            // }

            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                showAddShippingAddressForm: true,
                xStandardizationNotRequired: false,
                // xGoogleStandardizationNotRequired: false,
                userInputAddress,
            };
            const actions = {
                showContinueCTA: true,
                showCancelCTA: isUserLoggedIn,
                showContinueToPaymentCTA: false,
                showContinueToReviewCTA: false,
            };

            return { ...state, ...actions, shipToHomeInfo };
        }

        /**
         * Show edit billing address form with address
         */
        case actionTypes.EDIT_BILLING_ADDRESS: {
            const { context } = action;
            const internationalCountries = get(context, 'preferences.billingCountriesList');
            const currentAddress = get(state, 'shipToHomeInfo.userInputAddress.address', {});
            const userInputAddress = ShippingService.getEmptySTHFormInfo(
                true,
                internationalCountries
            );
            if (!isEmpty(action.address)) {
                userInputAddress.address = action.address;
                if (!get(userInputAddress, 'address.firstName') && currentAddress.firstName) {
                    userInputAddress.address.firstName = currentAddress.firstName;
                }
                if (!get(userInputAddress, 'address.lastName') && currentAddress.lastName) {
                    userInputAddress.address.lastName = currentAddress.lastName;
                }
                if (!get(userInputAddress, 'address.lineTwo') && currentAddress.lineTwo) {
                    userInputAddress.address.lineTwo = currentAddress.lineTwo;
                }
                if (!get(userInputAddress, 'address.lineThree') && currentAddress.lineThree) {
                    userInputAddress.address.lineThree = currentAddress.lineThree;
                }
                if (!get(userInputAddress, 'address.provience') && currentAddress.provience) {
                    userInputAddress.address.provience = currentAddress.provience;
                }
                if (!get(userInputAddress, 'address.state') && currentAddress.state) {
                    userInputAddress.address.state = currentAddress.state;
                }
                if (!get(userInputAddress, 'address.company') && currentAddress.company) {
                    userInputAddress.address.company = currentAddress.company;
                }
                const phoneNumber = get(userInputAddress, 'address.phone') || currentAddress.phone;
                const country = get(userInputAddress, 'address.country') || currentAddress.country;
                userInputAddress.address.phone = ShippingService.getFormattedPhoneNumber(
                    phoneNumber
                );
                const militaryAddressTypeCountry = ShippingService.isMilitaryAddress(country);
                if (country === 'AP' || militaryAddressTypeCountry) {
                    userInputAddress.address.country =
                        userInputAddress.address.militaryAddressType ||
                        userInputAddress.address.city;
                    userInputAddress.usStateList = ShippingService.getArmedForcesStateList();
                } else if (country === 'CA') {
                    userInputAddress.usStateList = ShippingService.getCanadaStateList();
                    userInputAddress.isInternationalAddress = true;
                } else if (country !== 'US') {
                    userInputAddress.usStateList = [];
                    userInputAddress.isInternationalAddress = true;
                }
            }
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                xStandardizationNotRequired: false,
                userInputAddress,
            };

            return { ...state, shipToHomeInfo };
        }

        /**
         * Cancel add/edit shipping address form
         */
        case actionTypes.CANCEL_ADD_EDIT_SHIPPING_ADDRESS: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                showAddShippingAddressForm: false,
                xStandardizationNotRequired: false,
                // xGoogleStandardizationNotRequired: false,
            };
            const actions = {
                showCancelCTA: false,
                showContinueCTA: false,
                showContinueToPaymentCTA: !state.isLoadedFromReview,
                showContinueToReviewCTA: state.isLoadedFromReview,
            };

            return { ...state, ...actions, shipToHomeInfo };
        }

        /**
         * add new shipping address for register user
         */
        case actionTypes.ADD_SHIPPING_ADDRESS: {
            /**
             * Init STH form
             */
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                showAddShippingAddressForm: true,
                xStandardizationNotRequired: false,
                // xGoogleStandardizationNotRequired: false,
                userInputAddress: ShippingService.getEmptySTHFormInfo(
                    false,
                    false,
                    state.isTruckableItemInOrder,
                    User.isUserLoggedIn()
                ),
            };
            const actions = {
                showContinueCTA: true,
                showCancelCTA: true,
                showContinueToPaymentCTA: false,
                showContinueToReviewCTA: false,
            };

            return { ...state, ...actions, shipToHomeInfo };
        }

        /**
         * Show delete shipping address warning message
         */
        case actionTypes.SHOW_DELETE_SHIPPING_ADDRESS_WARNING: {
            const deleteShippingAddressInfo = {};
            if (action.shippingAddressId) {
                deleteShippingAddressInfo.showWarning = true;
                deleteShippingAddressInfo.shippingAddressId = action.shippingAddressId;
                deleteShippingAddressInfo.isDefault = action.isDefault;
            }
            const shipToHomeInfo = { ...state.shipToHomeInfo, deleteShippingAddressInfo };

            return { ...state, shipToHomeInfo };
        }

        /**
         * show or hide view more address link
         */
        case actionTypes.SHOW_ALL_SHIPPING_ADDRESSES: {
            const shipToHomeInfo = {
                ...state.shipToHomeInfo,
                showViewMoreAddressLink: !action.value,
                showAddNewAddressLink: action.value,
            };
            return { ...state, shipToHomeInfo };
        }

        /**
         * Update express checkout shipping address
         */
        case actionTypes.SET_EXPRESS_CHECKOUT_ADDRESS: {
            const { address } = state.shipToHomeInfo.userInputAddress;
            address.expressCheckout = action.value;
            const userInputAddress = { ...state.shipToHomeInfo.userInputAddress, address };
            const shipToHomeInfo = { ...state.shipToHomeInfo, userInputAddress };
            return { ...state, shipToHomeInfo };
        }

        /**
         * Update street address check
         */
        case actionTypes.SET_STREET_ADDRESS_CHECK_EXECUTED: {
            const { userInputAddress } = state.shipToHomeInfo;
            userInputAddress.isStreetAddressCheckExecuted = action.value;
            const shipToHomeInfo = { ...state.shipToHomeInfo, userInputAddress };
            return { ...state, shipToHomeInfo };
        }

        /**
         * Update selected address
         */
        case actionTypes.UPDATE_SELECTED_ADDRESS: {
            const { userAddressList } = state.shipToHomeInfo;
            map(userAddressList, (address) => {
                // eslint-disable-next-line no-param-reassign
                address.selected = address.source.id === action.addressId;
            });
            let { hideDeliveryInfo } = state.shipToHomeInfo;
            let { showContinueCTA } = state;
            let { showContinueToPaymentCTA } = state;
            let { showContinueToReviewCTA } = state;
            if (action.hideDeliveryInfo) {
                hideDeliveryInfo = true;
                showContinueCTA = true;
                showContinueToPaymentCTA = false;
                showContinueToReviewCTA = false;
            }
            const shipToHomeInfo = { ...state.shipToHomeInfo, userAddressList, hideDeliveryInfo };
            return {
                ...state,
                shipToHomeInfo,
                showContinueCTA,
                showContinueToPaymentCTA,
                showContinueToReviewCTA,
            };
        }

        /**
         * Update shipping method if user apply or remove coupons from order summary
         */
        case actionTypes.UPDATE_SHIPPING_METHOD_LIST: {
            const { orderDetails } = action;
            let updatedState = state;
            if (state.shippingType === 'HOME') {
                const orderShippingAddress = ShippingService.getOrderShipToHomeAddress(
                    orderDetails
                );
                if (!isEmpty(orderShippingAddress)) {
                    const serviceLevels = get(orderShippingAddress, 'serviceLevels.data', []);
                    let shippingMethods = [];
                    if (!isEmpty(serviceLevels)) {
                        shippingMethods = ShippingService.getOrderShippingMethodUI(
                            orderShippingAddress.serviceLevels
                        );
                    }
                    const shipToHomeInfo = { ...state.shipToHomeInfo, shippingMethods };
                    updatedState = { ...state, shipToHomeInfo };
                }
            } else {
                const shippingMethod = { ...state.shipToStoreInfo.shippingMethod };
                shippingMethod.message = ShippingService.getShipToStoreCharge(orderDetails);
                const shipToStoreInfo = { ...state.shipToStoreInfo, shippingMethod };
                updatedState = { ...state, shipToStoreInfo };
            }
            return { ...state, ...updatedState };
        }

        case actionTypes.CLEAR_USER_INPUT_ADDRESS: {
            const shipToHomeInfo = { ...state.shipToHomeInfo };
            if (isEmpty(shipToHomeInfo.userInputAddress)) {
                return state;
            }
            const { context } = action;
            const internationalCountries = get(context, 'preferences.billingCountriesList', false);
            shipToHomeInfo.userInputAddress.address = ShippingService.getEmptySTHFormInfo(
                true,
                internationalCountries
            );
            return { ...state, shipToHomeInfo };
        }

        default:
            return state;
    }
}
